"use client";

import {
  Bluetooth,
  Check,
  ChevronDown,
  Cpu,
  EthernetPort,
  Globe,
  type LucideIcon,
  Radio,
  RefreshCw,
  Satellite,
  Smartphone,
  Unplug,
  Wifi,
} from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";

import { useFixedDropdownPlacement } from "../hooks/use-fixed-dropdown-placement";
import {
  CONNECTIVITY_PROTOCOL_OPTIONS,
  type TransmissionMode,
} from "../schema/datalogger.schema";

const PROTOCOL_ICONS: Record<TransmissionMode, LucideIcon> = {
  mqtt: Radio,
  http: Globe,
  websocket: Unplug,
  polling: RefreshCw,
  ethernet: EthernetPort,
  wifi: Wifi,
  cellular: Smartphone,
  bluetooth_serial: Bluetooth,
  modbus_tcp: Cpu,
  lora: Satellite,
};

const triggerClass =
  "ui-control flex h-10 min-h-10 w-full items-center justify-between gap-ds-2 rounded-control px-ds-3 text-left text-caption text-foreground outline-none transition-[box-shadow,border-color,background-color] focus-visible:ring-2 focus-visible:ring-primary/20";

type ProtocolSelectProps = {
  value: TransmissionMode;
  onChange: (mode: TransmissionMode) => void;
  id?: string;
};

export function ProtocolSelect({ value, onChange, id: idProp }: ProtocolSelectProps) {
  const autoId = useId();
  const listId = `${idProp ?? autoId}-protocol-list`;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const placement = useFixedDropdownPlacement(open, anchorRef, "match-anchor");

  useEffect(() => {
    function onDoc(e: globalThis.MouseEvent) {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || menuRef.current?.contains(t)) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = CONNECTIVITY_PROTOCOL_OPTIONS.find((o) => o.value === value);
  const SelectedIcon = selected ? PROTOCOL_ICONS[selected.value] : Radio;

  const list =
    open && placement ? (
      <ul
        ref={menuRef}
        id={listId}
        role="listbox"
        style={{
          position: "fixed",
          top: placement.top,
          left: placement.left,
          width: placement.width,
          maxHeight: placement.maxHeight,
          zIndex: 400,
        }}
        className="overflow-auto rounded-control border border-card-border bg-surface-primary py-ds-1 shadow-card"
      >
        {CONNECTIVITY_PROTOCOL_OPTIONS.map((o) => {
          const Icon = PROTOCOL_ICONS[o.value];
          const isSelected = o.value === value;
          return (
            <li key={o.value} role="option" aria-selected={isSelected}>
              <button
                type="button"
                className={cn(
                  "flex w-full items-center gap-ds-2 px-ds-3 py-ds-2 text-left text-caption transition-colors",
                  isSelected
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-muted-bg/50",
                )}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted-bg/50 text-muted dark:bg-muted-bg/40">
                  <Icon className="h-4 w-4 text-foreground/85" aria-hidden />
                </span>
                <span className="min-w-0 flex-1 font-medium">{o.label}</span>
                {isSelected ? <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden /> : null}
              </button>
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <>
      <div className="relative w-full">
        <button
          ref={anchorRef}
          type="button"
          id={idProp ?? autoId}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          className={triggerClass}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="flex min-w-0 items-center gap-ds-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-neutral-700 dark:bg-muted-bg/60 dark:text-foreground">
              <SelectedIcon className="h-4 w-4 shrink-0" aria-hidden />
            </span>
            <span className="min-w-0 truncate font-medium">{selected?.label ?? value}</span>
          </span>
          <ChevronDown
            className={cn("h-4 w-4 shrink-0 text-neutral-500 transition-transform", open && "rotate-180")}
            aria-hidden
          />
        </button>
      </div>
      {typeof document !== "undefined" && list ? createPortal(list, document.body) : null}
    </>
  );
}
